import { User } from '@choco-admin/types';
import { TableRow, TableCell, Button, Link, Chip } from '@heroui/react';
import React, { useState, useEffect } from 'react';
import {
  FiEdit,
  FiMessageCircle,
  FiUser,
  FiMail,
  FiPhone,
  FiShoppingBag,
  FiTruck,
  FiKey,
  FiHelpCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  DataTablePagination,
  LoadingTable,
  LoadingTableColumn,
} from '../../../../components/Table';
import { UserStatus } from '../../../../components/UserStatus';
import { useDebounce } from '../../../../hooks';
import { DEBOUNCE_TIME } from '../../constants';
import { getLoginMethodDisplayConfig } from '../../utils/loginMethodDisplay';

import { TableControls } from './components';

interface UsersTableProps {
  users: User[];
  totalUsers: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  searchTerm: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSearch: (term: string) => void;
  onRefresh: () => void;
  /** Override total pages when the caller knows it (e.g. cursor-based pagination). */
  totalPages?: number;
}

const renderBuyerLinks = (
  buyers?: Array<{
    id: string;
    name: string;
    referenceId?: string;
    fullAddress?: string;
  }>
) => {
  if (!buyers || buyers.length === 0) {
    return <span className="text-default-500">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {buyers.map((buyer, index) => (
        <React.Fragment key={buyer.id}>
          <Link
            href={`/buyers/${buyer.id}`}
            className="text-primary hover:text-primary/80 text-sm"
          >
            {buyer.name}
          </Link>
          {index < buyers.length - 1 && (
            <span className="text-default-500 text-sm">, </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const getLoginMethodDisplay = (user: User) => {
  const displayConfig = getLoginMethodDisplayConfig(user.loginMethod);

  const iconClassName = 'h-3.5 w-3.5';
  const getIcon = () => {
    switch (displayConfig.icon) {
      case 'phone':
        return <FiPhone className={iconClassName} />;
      case 'email':
        return <FiMail className={iconClassName} />;
      case 'emailAndPhone':
        return (
          <div className="flex items-center gap-0.5">
            <FiMail className="h-3 w-3" />
            <FiPhone className="h-3 w-3" />
          </div>
        );
      case 'sso':
        return <FiKey className={iconClassName} />;
      case 'unknown':
        return <FiHelpCircle className={iconClassName} />;
      default:
        return null;
    }
  };

  return (
    <Chip
      size="sm"
      variant="flat"
      color={displayConfig.color}
      startContent={getIcon()}
      aria-label={displayConfig.ariaLabel}
    >
      {displayConfig.label}
    </Chip>
  );
};

const getUserTypeDisplay = (user: User) => {
  const hasBuyers = user.linkedBuyers && user.linkedBuyers.length > 0;
  const isSupplier = user.supplier || user.supplierId;

  if (!hasBuyers && !isSupplier) {
    return <span className="text-default-500 text-sm">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {hasBuyers && (
        <Chip
          size="sm"
          variant="flat"
          color="success"
          startContent={<FiShoppingBag className="h-3 w-3" />}
        >
          {user.linkedBuyers.length === 1
            ? 'Buyer'
            : `${user.linkedBuyers.length} Buyers`}
        </Chip>
      )}
      {isSupplier && (
        <Chip
          size="sm"
          variant="flat"
          color="warning"
          startContent={<FiTruck className="h-3 w-3" />}
        >
          Distributor
        </Chip>
      )}
    </div>
  );
};

const loadingTableColumns: LoadingTableColumn[] = [
  { label: 'User', className: 'min-w-48' },
  { label: 'Buyer Entities', className: 'min-w-40 hidden lg:table-cell' },
  { label: 'Distributor Entity', className: 'min-w-40 hidden md:table-cell' },
  { label: 'Phone', className: 'min-w-32 hidden sm:table-cell' },
  { label: 'Email', className: 'min-w-48 hidden xl:table-cell' },
  { label: 'Login Method', className: 'min-w-36 hidden lg:table-cell' },
  { label: 'Locale', className: 'min-w-20 hidden md:table-cell' },
  { label: 'User Type', className: 'min-w-32 hidden sm:table-cell' },
  { label: 'Actions', className: 'w-32 sticky right-0 bg-default-100 z-10' },
];

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  totalUsers,
  currentPage,
  pageSize,
  loading,
  searchTerm,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onRefresh,
  totalPages: totalPagesProp,
}) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(searchTerm);
  const debouncedSearchTerm = useDebounce(inputValue, DEBOUNCE_TIME);

  // Effect to handle debounced search
  useEffect(() => {
    onSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearch]);

  // Update local state when search term changes from outside
  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  const handleSearchChange = (value: string) => {
    setInputValue(value);
  };

  const handleEditUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleViewChats = (userId: string) => {
    navigate(`/chats?search=${userId}`);
  };

  const totalPages = totalPagesProp ?? Math.ceil(totalUsers / pageSize);

  const renderRow = (user: User) => (
    <TableRow key={user.id}>
      <TableCell>
        <div
          className="flex flex-col gap-1 cursor-pointer hover:bg-default-50 rounded-md p-2 -m-2 transition-colors"
          onClick={() => handleEditUser(user.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleEditUser(user.id);
            }
          }}
        >
          <span className="font-medium text-foreground hover:text-primary transition-colors">
            {user.name || 'Unknown User'}
          </span>
          <UserStatus
            hasRequestedToBeDeleted={user.hasRequestedToBeDeleted || false}
            pending={user.pending || false}
            size="sm"
            variant="chip"
          />
        </div>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {renderBuyerLinks(user.linkedBuyers)}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {user.supplierId ? (
          <Link
            href={`/suppliers/${user.supplierId}`}
            className="text-primary hover:text-primary/80"
          >
            {user.supplierName}
          </Link>
        ) : (
          <span className="text-default-500">None</span>
        )}
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <span className="text-sm text-foreground">{user.phone || 'N/A'}</span>
      </TableCell>
      <TableCell className="hidden xl:table-cell">
        <span className="text-sm text-foreground">{user.email || 'N/A'}</span>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {getLoginMethodDisplay(user)}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-foreground">{user.locale || 'N/A'}</span>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        {getUserTypeDisplay(user)}
      </TableCell>
      <TableCell className="sticky right-0 bg-content1 z-10">
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="flat"
            color="primary"
            isIconOnly
            aria-label="Edit user"
            onPress={() => handleEditUser(user.id)}
            className="min-w-8"
          >
            <FiEdit className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="flat"
            color="secondary"
            isIconOnly
            aria-label="View chats"
            onPress={() => handleViewChats(user.id)}
            className="min-w-8"
          >
            <FiMessageCircle className="h-3 w-3" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      <TableControls
        searchValue={inputValue}
        pageSize={pageSize}
        onSearchChange={handleSearchChange}
        onPageSizeChange={onPageSizeChange}
      />

      <LoadingTable
        columns={loadingTableColumns}
        ariaLabel="Users table"
        isLoading={loading}
        isEmpty={users.length === 0}
        data={users}
        renderRow={(user) => renderRow(user as User)}
        emptyState={{
          icon: FiUser,
          iconBgClass: 'bg-primary-50',
          iconColorClass: 'text-primary-500',
          title: 'No users found',
          description:
            "Try adjusting your search term to find the users you're looking for.",
        }}
      />

      <DataTablePagination
        totalItems={totalUsers}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={onPageChange}
        itemName="users"
        isLoading={loading}
      />
    </div>
  );
};
